import { inject } from '@angular/core';

import { ComponentsService } from '../../services/components.service';
import {
  LayoutAiComponent,
  LayoutAiShowOptions,
} from '../layout-ai/layout-ai.component';

/**
 * Layout selection and AI layout behavior shared by the rise-layout-* editors.
 * Subclasses load and persist their own settings via {@link save}.
 */
export abstract class LayoutEditorBase {
  // Rise admins only, and not in production or beta yet
  readonly AI_LAYOUT_ROLE = 'sa !PROD !BETA';

  public componentId: string;
  public layout: string;
  protected componentLayout: any;

  private previousCustomLayout: any = null;
  private readonly layoutComponentsService = inject(ComponentsService);

  /** The rise-layout-* component the editor configures. */
  protected abstract readonly layoutComponent: string;

  protected abstract save(): void;

  get isCustomLayout(): boolean {
    return this.layout === ComponentsService.LAYOUT_TYPE_CUSTOM;
  }

  protected loadLayoutSelection(defaultLayoutId: string): void {
    this.previousCustomLayout = null;
    this.layout = ComponentsService.getLayoutSelection(
      this.componentLayout,
      defaultLayoutId,
    );
  }

  protected applyLayoutSelection(): void {
    if (
      !this.isCustomLayout &&
      this.componentLayout?.type === ComponentsService.LAYOUT_TYPE_CUSTOM
    ) {
      this.previousCustomLayout = this.componentLayout;
    }

    this.componentLayout = ComponentsService.applyLayoutSelection(
      this.componentLayout,
      this.layout,
    );
  }

  onLayoutGenerated(layoutHtml: string, usedAttributes?: string[]): void {
    this.componentLayout = ComponentsService.buildCustomLayout(
      layoutHtml,
      this.layoutComponent,
      usedAttributes,
    );
    this.layout = ComponentsService.LAYOUT_TYPE_CUSTOM;
    this.save();
  }

  usesAttribute(attribute: string): boolean {
    return ComponentsService.usesLayoutAttribute(
      this.componentLayout,
      attribute,
    );
  }

  openLayoutAi(): void {
    if (this.previousCustomLayout && !this.isCustomLayout) {
      this.onLayoutGenerated(
        this.previousCustomLayout.layoutHtml,
        this.previousCustomLayout.usedAttributes,
      );
      return;
    }

    this.layoutComponentsService.editComponent(
      { type: LayoutAiComponent.TYPE },
      {
        component: this.layoutComponent.replace(/^rise-layout-/, ''),
        componentId: this.componentId,
        onGenerated: (layout) =>
          this.onLayoutGenerated(layout.html, layout.usedAttributes),
      } satisfies LayoutAiShowOptions,
    );
  }
}
